import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CheckCircle, XCircle, FileText, Clock, PencilSimple, Check, X, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { useSort, sortRows, SortHeader } from "@/components/SortHeader";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ReleasePaymentDialog from "@/components/ReleasePaymentDialog";
import RejectRequestDialog from "@/components/RejectRequestDialog";
import { useInvoiceViewer } from "@/components/InvoiceViewer";

const fmtCur = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtDate = (s) => s ? new Date(s).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
const fmtDateOnly = (s) => s ? new Date(s).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric" }) : "—";

const PAGE_SIZE = 20;
const releasedKey = (r) => { const t = new Date(r.released_at || r.decided_at || r.released_on || "").getTime(); return isNaN(t) ? 0 : t; };

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

// Inline editable note for pending rows.
function NoteCell({ row, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(row.note || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/approvals/${row.id}/note`, { note: value });
      toast.success("Note saved");
      setEditing(false);
      onSaved?.(row.id, value);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save note");
    } finally { setSaving(false); }
  };

  if (!editing) {
    return (
      <div className="flex items-start gap-1.5 min-w-[180px] max-w-[260px]">
        <div className="text-xs text-slate-700 italic flex-1 whitespace-pre-wrap break-words" data-testid={`approval-note-${row.id}`}>
          {row.note ? row.note : <span className="not-italic text-slate-400">—</span>}
        </div>
        {canEdit && (
          <button
            onClick={() => { setValue(row.note || ""); setEditing(true); }}
            className="shrink-0 p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
            data-testid={`approval-note-edit-${row.id}`}
            title="Edit note"
          ><PencilSimple size={12} /></button>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-start gap-1 min-w-[220px]">
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={2}
        autoFocus
        disabled={saving}
        className="flex-1 text-xs border border-slate-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
        data-testid={`approval-note-input-${row.id}`}
      />
      <div className="flex flex-col gap-0.5 shrink-0">
        <button
          onClick={save}
          disabled={saving}
          className="p-1 bg-emerald-600 text-white hover:bg-emerald-700 rounded disabled:opacity-50"
          data-testid={`approval-note-save-${row.id}`}
          title="Save"
        ><Check size={12} /></button>
        <button
          onClick={() => setEditing(false)}
          disabled={saving}
          className="p-1 bg-white border border-slate-200 text-slate-500 hover:bg-slate-50 rounded"
          data-testid={`approval-note-cancel-${row.id}`}
          title="Cancel"
        ><X size={12} /></button>
      </div>
    </div>
  );
}

export default function Approvals() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const { openInvoice } = useInvoiceViewer();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [releaseItem, setReleaseItem] = useState(null);
  const [rejectItem, setRejectItem] = useState(null);
  const [sort, toggleSort, setSort] = useSort("requested_at");
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/approvals", { params: { status: tab } });
      setItems(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSort({ key: tab === "approved" ? "released_at" : "requested_at", dir: "desc" }); }, [tab, setSort]);
  useEffect(() => { setPage(1); }, [tab, sort]);

  const sorted = useMemo(
    () => sortRows(items, sort.key === "released_at" ? releasedKey : (r) => r.requested_at, sort.dir),
    [items, sort]
  );
  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageItems = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Per-tab column plan: Notes only in Pending; Released On only in Approved.
  const showNotes = tab === "pending";
  const showReleasedOn = tab === "approved";
  // Base = 9 fixed columns (Creator, Campaign, Payment type, Amount, TDS, Net,
  // Invoice, Requested, Status) + per-tab + admin-only Action.
  const colCount = 9 + (showNotes ? 1 : 0) + (showReleasedOn ? 1 : 0) + (isAdmin && tab === "pending" ? 1 : 0);

  const updateRowNote = (rid, note) => setItems((xs) => xs.map((x) => x.id === rid ? { ...x, note } : x));

  // Summary stats scoped to the currently active tab.
  const tabSummary = useMemo(() => {
    const net = items.reduce((s, r) => s + Number(r.net_amount || 0), 0);
    const labels = {
      pending:  { count: "Total Pending Payments",  amount: "Total Amount to be Released" },
      approved: { count: "Total Approved Payments", amount: "Total Amount Released" },
      rejected: { count: "Total Rejected Payments", amount: "Total Rejected Amount" },
    };
    return { count: items.length, net, labels: labels[tab] || labels.pending };
  }, [items, tab]);

  const openCreator = (r) => {
    if (!r.campaign_id || !r.creator_id) return;
    navigate(`/campaigns/${r.campaign_id}?creator=${r.creator_id}`);
  };

  return (
    <div className="space-y-5" data-testid="approvals-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Payments</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Approvals</h1>
        <p className="text-xs sm:text-sm text-slate-500 mt-1">
          {isAdmin ? "Pending payment requests across all campaigns." : "Your pending payment requests."}
        </p>
        <div
          className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-slate-600"
          data-testid="approvals-summary"
        >
          <div>
            <span className="text-slate-500">{tabSummary.labels.count}:</span>{" "}
            <span className="font-semibold text-slate-900" data-testid="approvals-summary-count">
              {tabSummary.count}
            </span>
          </div>
          <div className="text-slate-300">|</div>
          <div>
            <span className="text-slate-500">{tabSummary.labels.amount}:</span>{" "}
            <span className="font-semibold text-emerald-700 font-mono" data-testid="approvals-summary-net">
              {fmtCur(tabSummary.net)}
            </span>
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        {["pending", "approved", "rejected"].map((s) => (
          <button
            key={s}
            onClick={() => setTab(s)}
            data-testid={`approvals-tab-${s}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${tab === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      <Card className="border border-slate-200 shadow-none overflow-hidden">
        <div className="overflow-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Creator</TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead>Payment type</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">TDS</TableHead>
                <TableHead className="text-right">Net</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead><SortHeader label="Requested" sortKey="requested_at" sort={sort} onToggle={toggleSort} testid="approvals-sort-toggle" /></TableHead>
                {showNotes && <TableHead>Notes</TableHead>}
                {showReleasedOn && <TableHead><SortHeader label="Released On" sortKey="released_at" sort={sort} onToggle={toggleSort} testid="approvals-sort-released" /></TableHead>}
                <TableHead>Status</TableHead>
                {isAdmin && tab === "pending" && <TableHead className="text-right w-[180px]">Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={colCount} className="text-center text-slate-500 py-10">Loading…</TableCell></TableRow>
              ) : sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} className="text-center py-12">
                    <Clock size={28} className="mx-auto text-slate-300 mb-2" weight="duotone" />
                    <div className="text-sm text-slate-500">No {tab} requests.</div>
                  </TableCell>
                </TableRow>
              ) : (
                pageItems.map((r) => {
                  const canEditNote = isAdmin || r.requested_by_id === user?.id;
                  return (
                  <TableRow key={r.id} className="hover:bg-slate-50/60" data-testid={`approval-row-${r.id}`}>
                    <TableCell className="font-medium text-slate-900">
                      {r.entity_kind === "cost" || r.cost_id ? (
                        <>
                          {r.cost_type_snapshot || "Additional cost"}
                          {r.poc_name_snapshot && <div className="text-xs text-slate-500 font-normal">{r.poc_name_snapshot}</div>}
                        </>
                      ) : r.creator_id && r.campaign_id ? (
                        <button
                          type="button"
                          onClick={() => openCreator(r)}
                          className="text-blue-600 hover:text-blue-700 hover:underline underline-offset-2 font-medium"
                          data-testid={`approval-creator-link-${r.id}`}
                          title="Open creator profile"
                        >
                          @{r.creator_username_snapshot}
                        </button>
                      ) : (
                        <>@{r.creator_username_snapshot}</>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-700">
                      <div className="font-medium">{r.brand_name_snapshot || "—"}</div>
                      {r.campaign_name_snapshot && <div className="text-slate-500">{r.campaign_name_snapshot}</div>}
                    </TableCell>
                    <TableCell className="text-sm">{r.payment_type}</TableCell>
                    <TableCell className="text-right font-mono text-sm font-semibold">{fmtCur(r.amount)}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-rose-700">− {fmtCur(r.tds_amount)}</TableCell>
                    <TableCell className="text-right font-mono text-sm font-semibold text-emerald-700">{fmtCur(r.net_amount)}</TableCell>
                    <TableCell>
                      {r.invoice_file_id ? (
                        <button
                          type="button"
                          onClick={() => openInvoice(r.invoice_file_id, r.invoice_filename)}
                          className="text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 text-xs"
                          data-testid={`approval-invoice-link-${r.id}`}
                        >
                          <FileText size={11} /> {r.invoice_type}
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400">{r.invoice_type}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">
                      <div>{fmtDate(r.requested_at)}</div>
                      <div className="text-slate-500">Requested by: <span className="font-medium text-slate-700">{r.requested_by_name || "—"}</span></div>
                    </TableCell>
                    {showNotes && (
                      <TableCell>
                        <NoteCell row={r} canEdit={canEditNote} onSaved={updateRowNote} />
                      </TableCell>
                    )}
                    {showReleasedOn && (
                      <TableCell className="text-xs text-slate-600" data-testid={`approval-released-on-${r.id}`}>
                        {r.released_at ? fmtDate(r.released_at)
                          : r.decided_at ? fmtDate(r.decided_at)
                          : r.released_on ? fmtDateOnly(r.released_on)
                          : <span className="text-slate-300">—</span>}
                      </TableCell>
                    )}
                    <TableCell>
                      <span className={`text-[11px] rounded-full px-2 py-0.5 font-medium border ${STATUS_STYLES[r.status] || ""}`}>{r.status}</span>
                      {r.status === "rejected" && r.rejection_note && (
                        <div className="text-[11px] text-slate-500 mt-1 italic max-w-[200px] line-clamp-2" title={r.rejection_note}>“{r.rejection_note}”</div>
                      )}
                    </TableCell>
                    {isAdmin && tab === "pending" && (
                      <TableCell className="text-right">
                        <div className="inline-flex gap-1">
                          <Button size="sm" onClick={() => setReleaseItem(r)} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white px-3" data-testid={`release-btn-${r.id}`}>
                            <CheckCircle size={14} className="mr-1" /> Release
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setRejectItem(r)} className="h-8 border-rose-200 text-rose-600 hover:bg-rose-50 px-3" data-testid={`reject-btn-${r.id}`}>
                            <XCircle size={14} className="mr-1" /> Reject
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );})
              )}
            </TableBody>
          </Table>
        </div>
        {sorted.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-t border-slate-200 bg-slate-50/60 text-xs text-slate-600" data-testid="approvals-pager">
            <span>Showing <b>{(safePage - 1) * PAGE_SIZE + 1}</b>–<b>{Math.min(safePage * PAGE_SIZE, sorted.length)}</b> of <b>{sorted.length}</b></span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(safePage - 1)} disabled={safePage <= 1} className="h-8 border-slate-300" data-testid="approvals-pager-prev">
                <CaretLeft size={12} className="mr-1" /> Previous
              </Button>
              <span className="font-mono" data-testid="approvals-pager-position">Page {safePage} of {pageCount}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(safePage + 1)} disabled={safePage >= pageCount} className="h-8 border-slate-300" data-testid="approvals-pager-next">
                Next <CaretRight size={12} className="ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      <ReleasePaymentDialog open={!!releaseItem} onOpenChange={(v) => !v && setReleaseItem(null)} request={releaseItem} onReleased={load} />
      <RejectRequestDialog open={!!rejectItem} onOpenChange={(v) => !v && setRejectItem(null)} request={rejectItem} onRejected={load} />
    </div>
  );
}
